// src/services/api.js
// Central API client — all calls to the GAAFrfid backend go through here

import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
    baseURL: BASE_URL,
    timeout: 30000,
    headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('gaafrfid_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

api.interceptors.response.use(
    (res) => res,
    (err) => {
        if (err.response?.status === 401) {
            const isLoginRequest = err.config?.url?.includes('/auth/login');
            const isAlreadyOnLogin = window.location.pathname === '/login';
            if (!isLoginRequest && !isAlreadyOnLogin) {
                localStorage.removeItem('gaafrfid_token');
                localStorage.removeItem('gaafrfid_user');
                window.location.href = '/login';
            }
        }
        return Promise.reject(err);
    }
);

export const authAPI = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    me: () => api.get('/auth/me'),
    refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
};

export const companiesAPI = {
    list: () => api.get('/companies'),
    get: (id) => api.get(`/companies/${id}`),
    create: (data) => api.post('/companies', data),
    update: (id, data) => api.put(`/companies/${id}`, data),
    getWarehouseLimit: (companyId) => api.get(`/companies/${companyId}/warehouse-limit`),
};

export const warehousesAPI = {
    list: (companyId) => api.get('/warehouses', { params: { companyId } }),
    get: (id) => api.get(`/warehouses/${id}`),
    create: (data) => api.post('/warehouses', data),
    update: (id, data) => api.put(`/warehouses/${id}`, data),
    delete: (id) => api.put(`/warehouses/${id}`, { status: 'inactive' }),
};

export const certificatesAPI = {
    list: (companyId) => api.get('/certificates', { params: { companyId } }),
    provision: (data) => api.post('/certificates/provision', data),
    revoke: (id) => api.post(`/certificates/${id}/revoke`),
};

export const productsAPI = {
    list: (companyId) => api.get('/products', { params: { companyId } }),
    get: (id) => api.get(`/products/${id}`),
    create: (data) => api.post('/products', data),
    update: (id, data) => api.put(`/products/${id}`, data),
    delete: (id) => api.put(`/products/${id}`, { status: 'inactive' }),
    encodeTag: (id, data) => api.post(`/products/${id}/encode`, data),
    batchEncode: (data) => api.post('/products/batch-encode', data),
};

export const inventoryAPI = {
    get: (params) => api.get('/inventory', { params }),
    getItem: (itemId) => api.get(`/inventory/items/${itemId}`),
    getStats: (params) => api.get('/inventory/stats', { params }),
    getEvents: (params) => api.get('/inventory/events', { params }),
    getLowStock: (params) => api.get('/inventory/low-stock', { params }),
    updateWeight: (itemId, data) => api.post(`/inventory/items/${itemId}/weight`, data),
};

export const alertsAPI = {
    list: (params) => api.get('/alerts', { params }),
    updateSettings: (data) => api.put('/alerts/settings', data),
};

export const aiAPI = {
    chat: (data) => api.post('/ai/chat', data),
};

export const knowledgeBaseAPI = {
    list: (companyId) => api.get('/knowledge-base', { params: { companyId } }),
    create: (data) => api.post('/knowledge-base', data),
    update: (id, data) => api.put(`/knowledge-base/${id}`, data),
    delete: (id) => api.delete(`/knowledge-base/${id}`),
};

export const staffAPI = {
    list: (params) => api.get('/staff', { params }),
    get: (id) => api.get(`/staff/${id}`),
    create: (data) => api.post('/staff', data),
    update: (id, data) => api.put(`/staff/${id}`, data),
    encodeBadge: (id) => api.post(`/staff/${id}/encode-badge`),
};

export const attendanceAPI = {
    get: (params) => api.get('/attendance', { params }),
    getReport: (params) => api.get('/attendance/report', { params }),
    process: (data) => api.post('/attendance/process', data),
};

export const adsAPI = {
    list: (params) => api.get('/ads', { params }),
    create: (data) => api.post('/ads', data),
    update: (id, data) => api.put(`/ads/${id}`, data),
    delete: (id) => api.put(`/ads/${id}`, { status: 'inactive' }),
    performance: (id, params) => api.get(`/ads/${id}/performance`, { params }),
    recordImpression: (id, data) => api.post(`/ads/${id}/impression`, data),
};

export const billingAPI = {
    checkout: (data) => api.post('/billing/checkout', data),
    portal: (data) => api.post('/billing/portal', data),
    manual: (data) => api.post('/billing/manual', data),
    getSubscription: (params) => api.get('/billing/subscription', { params }),
    getInvoices: (params) => api.get('/billing/invoices', { params }),
    adminOverview: () => api.get('/billing/admin'),
};

export default api;
